import { Component, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { LookupsService } from '../../../api/services/lookups/lookups.service';
import { LookupsStore } from '../../../api/services/lookups/lookups.store';
import { NgApexchartsModule, ApexOptions } from 'ng-apexcharts';
import { RouterModule } from '@angular/router';
import { QuarterlyReportsStore } from '../../../api/services/reports/quarterly-reports/quarterly-reports.store';
import { SnackbarComponent, ToastType } from '../../../shared/components/layout/snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReportsService } from '../../../api/services/reports/reports.service';

@Component({
  selector: 'app-quarterly-report',
  templateUrl: './quarterly-report.component.html',
  styleUrl: './quarterly-report.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatOptionModule,
    MatSelectModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatToolbarModule,
    MatNativeDateModule,
    MatRippleModule,
    CommonModule,
    NgApexchartsModule,
    RouterModule,
  ],
})
export class QuarterlyReportComponent {
  readonly lookupsStore = inject(LookupsStore);
  readonly quarterlyReportsStore = inject(QuarterlyReportsStore);

  yearQuarters: any[] = [];
  filtersForm!: UntypedFormGroup;

  // Charts
  pieChartOptions: any = {};
  chartSeries: number[] = [];
  pieLabels: string[] = [];

  trainingMetricsChart: any = {};
  accountMetricsChart: any = {};
  websiteAnalyticsChart: any = {};
  orgCategoryPieChart: any = {};
  orgCategoryPieSeries: number[] = [];
  orgCategoryPieLabels: string[] = [];

  allStates: any[] = [];

  reportingPeriod: string = '';
  currentState: string = '';
  disableExport: boolean = false;

  // Monthly detail tables (course x language x refresher, one column per month)
  detailMonths: { key: string; label: string }[] = [];
  registrationDetailRows: any[] = [];
  completionDetailRows: any[] = [];

  reportMode: 'quarterly' | 'monthly' = 'quarterly';
  months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];
  years: number[] = [];

  private completedTrainingsPieColors = ['#29B494', '#211845', '#8F8BA1', '#98D9C7', '#ED7283', '#D4D4D4', '#908E9C', '#C2E7DE'];
  private orgIndustryPieColors = ['#808080', '#8F8BA1', '#D4D4D4', '#C1C1C1', '#171717', '#8F8BA1', '#29B494', '#C1E7DE', '#7DD0D6', '#B3CDFA', '#F6B4AE', '#FDE39A', '#AEDBB9', '#211845', '#FF0E0E'];
  private barChartColors = ['#211845', '#29B494'];

  constructor(
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private lookupsService: LookupsService,
    private reportsService: ReportsService,
  ) {
    this.lookupsService.getStates();

    var year = new Date().getFullYear();
    var month = new Date().getMonth() + 1;
    var quarter = Math.ceil(month / 3);

    for (var i = year; i >= 2018; i--) {
      this.years.push(i);
    }

    var yearQuarters: any[] = [];
    for (var i = year; i >= 2018; i--) {
      for (var j = 4; j >= 1; j--) {
        if (i === year && j > quarter) {
          continue;
        }
        yearQuarters.push({ value: `${i}Q${j}`, label: `${i} - Quarter ${j}` });
      }
    }
    this.yearQuarters = yearQuarters;

    var defaultToMonth = month === 1 ? 12 : month - 1;
    var defaultToYear = month === 1 ? year - 1 : year;
    var defaultFromMonth = defaultToMonth;
    var defaultFromYear = defaultToYear;

    this.filtersForm = this.fb.group({
      yearQuarter: [yearQuarters[1].value],
      state: [''],
      fromMonth: [defaultFromMonth],
      fromYear: [defaultFromYear],
      toMonth: [defaultToMonth],
      toYear: [defaultToYear],
    });

    this.reportingPeriod = yearQuarters[1].value.replace(/(\d{4})Q(\d)/, '$1 Quarter $2 Report');
    this.currentState = this.filtersForm.get('state')?.value ?? '';

    effect(() => {
      const states = this.lookupsStore.statesSignal();
      if (states.length == 1) {
        queueMicrotask(() => {
          this.allStates = states;
          this.filtersForm.patchValue({ state: this.allStates[0].text });
          this.currentState = this.filtersForm.get('state')?.value;
        });
      }
    });

    this.fetchData();

    // Training Metrics charts (pie + bar)
    effect(() => {
      const certData = this.quarterlyReportsStore.quarterCourseCertificateSignal();
      const regData = this.quarterlyReportsStore.quarterCourseRegistrationSignal();
      if (certData !== null && regData !== null) {
        queueMicrotask(() => {
          // Pie chart - Certificates by Training
          this.chartSeries = [];
          this.pieLabels = [];
          certData.learningPathTally.forEach((item: any) => {
            this.pieLabels.push(`${item.description1.name} (${this.formatNumber(item.count1.total)})`);
            this.chartSeries.push(item.count1.total);
          });

          this.pieChartOptions = {
            chart: { type: 'pie', width: '100%', height: '100%' },
            labels: this.pieLabels,
            colors: this.completedTrainingsPieColors,
          };

          // Training Metrics bar chart
          var displayMonths: string[] = [];
          var monthlyRegistrations: number[] = [];
          var monthlyCertificates: number[] = [];

          certData.monthTally.forEach((item: any) => {
            displayMonths.push(item.description1.name);
            monthlyCertificates.push(item.count1.total);
          });
          regData.monthTally.forEach((item: any) => {
            monthlyRegistrations.push(item.count1.total);
          });

          this.trainingMetricsChart = this.buildBarChart(
            [
              { name: 'Registrations', data: monthlyRegistrations },
              { name: 'Certificates', data: monthlyCertificates },
            ],
            displayMonths,
          );

          // Monthly detail tables broken down by course, language, refresher
          const regBreakdown = regData.breakdown ?? [];
          const certBreakdown = certData.breakdown ?? [];
          this.detailMonths = this.buildDetailMonths([...regBreakdown, ...certBreakdown]);
          this.registrationDetailRows = this.buildDetailRows(regBreakdown);
          this.completionDetailRows = this.buildDetailRows(certBreakdown);
        });
      }
    });

    // Account Metrics chart
    effect(() => {
      const data = this.quarterlyReportsStore.accountMetricsSignal();
      if (data) {
        queueMicrotask(() => {
          const labels = data.monthlyUserRegistrations.map((x: any) => x.label);
          const userRegs = data.monthlyUserRegistrations.map((x: any) => x.value);
          const orgRegs = data.monthlyOrgRegistrations.map((x: any) => x.value);

          this.accountMetricsChart = this.buildBarChart(
            [
              { name: 'User Registrations', data: userRegs },
              { name: 'Org Registrations', data: orgRegs },
            ],
            labels,
          );

          // Org category pie chart
          if (data.byIndustry && data.byIndustry.length > 0) {
            this.orgCategoryPieSeries = [];
            this.orgCategoryPieLabels = [];
            data.byIndustry.forEach((item: any) => {
              this.orgCategoryPieLabels.push(`${item.category} (${this.formatNumber(item.count)})`);
              this.orgCategoryPieSeries.push(item.count);
            });
            this.orgCategoryPieChart = {
              chart: { type: 'pie', width: '100%', height: '100%' },
              labels: this.orgCategoryPieLabels,
              colors: this.orgIndustryPieColors,
            };
          }
        });
      }
    });

    // Website Analytics chart
    effect(() => {
      const data = this.quarterlyReportsStore.websiteAnalyticsSignal();
      if (data) {
        queueMicrotask(() => {
          const labels = data.monthlyUsers.map((x: any) => x.label);
          const users = data.monthlyUsers.map((x: any) => x.value);
          const sessions = data.monthlySessions.map((x: any) => x.value);

          this.websiteAnalyticsChart = this.buildBarChart(
            [
              { name: 'Users', data: users },
              { name: 'Sessions', data: sessions },
            ],
            labels,
          );
        });
      }
    });

    this.filtersForm.valueChanges.subscribe((data) => {
      const selectedStateValue = this.filtersForm.get('state')?.value;
      const statesLookup = this.lookupsStore.statesSignal();
      const matchingState = statesLookup.find((state) => state['text'] === selectedStateValue);
      this.currentState = matchingState ? matchingState.label : '';
      this.fetchData();
      this.currentState = this.currentState ?? '';
      this.updateReportingPeriod();
    });
  }

  private buildBarChart(series: any[], categories: string[]): any {
    return {
      series,
      chart: { type: 'bar', height: 350, toolbar: { show: true } },
      dataLabels: {
        formatter: (val: any) => this.formatNumber(+val),
      },
      stroke: { show: true, width: 5, colors: ['transparent'] },
      plotOptions: { bar: { horizontal: false } },
      yaxis: {
        labels: { formatter: (val: any) => this.formatNumber(+val) },
      },
      xaxis: { type: 'category', categories },
      tooltip: {
        y: { formatter: (val: any) => this.formatNumber(+val) },
      },
      colors: this.barChartColors,
      legend: { position: 'top', horizontalAlign: 'left', offsetX: 40 },
    };
  }

  onModeChange() {
    this.fetchData();
    this.updateReportingPeriod();
  }

  updateReportingPeriod() {
    if (this.reportMode === 'monthly') {
      const raw = this.filtersForm.getRawValue();
      const fromMonthName = this.months.find((m) => m.value === raw.fromMonth)?.label ?? '';
      const toMonthName = this.months.find((m) => m.value === raw.toMonth)?.label ?? '';
      this.reportingPeriod = `${fromMonthName} ${raw.fromYear} - ${toMonthName} ${raw.toYear} Report`;
    } else {
      const raw = this.filtersForm.getRawValue();
      this.reportingPeriod = raw.yearQuarter.replace(/(\d{4})Q(\d)/, '$1 Quarter $2 Report');
    }
  }

  getSearchParams(allTime: boolean = false) {
    const rawParams = this.filtersForm.getRawValue();
    let fromMonth: number, fromYear: number, toMonth: number, toYear: number;

    if (this.reportMode === 'monthly') {
      fromMonth = rawParams.fromMonth;
      fromYear = rawParams.fromYear;
      toMonth = rawParams.toMonth;
      toYear = rawParams.toYear;
    } else {
      const year = parseInt(rawParams.yearQuarter.split('Q')[0]);
      const quarter = parseInt(rawParams.yearQuarter.split('Q')[1]);
      fromMonth = (quarter - 1) * 3 + 1;
      toMonth = quarter * 3;
      fromYear = year;
      toYear = year;
    }

    return {
      FromMonth: fromMonth,
      FromYear: fromYear,
      ToMonth: toMonth,
      ToYear: toYear,
      State: rawParams.state ?? '',
      AllTime: allTime,
    };
  }

  fetchData() {
    const params = this.getSearchParams(false);
    const paramsAllTime = this.getSearchParams(true);

    // Training metrics
    this.reportsService.getTotalCourseRegistration(paramsAllTime);
    this.reportsService.getTotalCourseCertificate(paramsAllTime);
    this.reportsService.getQuarterCourseRegistration(params);
    this.reportsService.getQuarterCourseCertificate(params);

    // Account metrics (includes user regs, org regs, by-industry)
    this.reportsService.getAccountMetrics(params);

    // Website analytics (includes users, sessions)
    this.reportsService.getWebsiteAnalytics(params);

    // Top performers
    this.reportsService.getTopOrganizations(params);
    this.reportsService.getTopDomains(params);
  }

  downloadReport() {
    const params = this.getSearchParams(false);
    params.State = params.State || '';

    if (!params.State) {
      this.snackBar.openFromComponent(SnackbarComponent, {
        duration: 3000,
        data: { toastType: ToastType.Error, message: `A state must be selected to export to PDF` },
      });
      return;
    }
    this.disableExport = true;
    this.snackBar.openFromComponent(SnackbarComponent, {
      duration: 3000,
      data: { toastType: ToastType.Success, message: `Downloading report, this may take up to 1 minute` },
    });
    this.reportsService
      .downloadQuarterlyReport(params)
      .then((res: any) => {
        this.disableExport = false;
      })
      .catch((error: any) => {
        this.disableExport = false;
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: `An error occurred while exporting the report` },
        });
      });
  }

  private buildDetailMonths(breakdown: any[]): { key: string; label: string }[] {
    const monthMap = new Map<string, string>();
    (breakdown ?? []).forEach((b) => {
      const key = `${b.year}-${String(b.month).padStart(2, '0')}`;
      if (!monthMap.has(key)) {
        monthMap.set(key, new Date(b.year, b.month - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' }));
      }
    });
    return [...monthMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, label]) => ({ key, label }));
  }

  private buildDetailRows(breakdown: any[]): any[] {
    const rowMap = new Map<string, any>();
    (breakdown ?? []).forEach((b) => {
      const monthKey = `${b.year}-${String(b.month).padStart(2, '0')}`;
      const rowKey = `${b.learningPath}||${b.language}||${b.isRefresher}`;
      let row = rowMap.get(rowKey);
      if (!row) {
        row = { learningPath: b.learningPath, language: b.language, isRefresher: b.isRefresher, counts: {}, total: 0 };
        rowMap.set(rowKey, row);
      }
      row.counts[monthKey] = (row.counts[monthKey] ?? 0) + b.count;
      row.total += b.count;
    });
    return [...rowMap.values()].sort(
      (a, b) =>
        a.learningPath.localeCompare(b.learningPath) ||
        a.language.localeCompare(b.language) ||
        Number(a.isRefresher) - Number(b.isRefresher),
    );
  }

  formatNumber(value: number): string {
    if (value == null) {
      return '';
    }
    const roundedValue = Math.round(value);
    return `${roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }
}
